import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../utils/supabase';

const QuickContactSummary = ({ onSectionChange }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contactedVendors, setContactedVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadInquiries = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const { data: inquiries, error } = await supabase
          .from('inquiries')
          .select(`
            id,
            vendor_id,
            message,
            status,
            created_at,
            updated_at,
            vendor_profiles!inner (
              id,
              business_name,
              category
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);

        if (error) {
          // Table might not exist yet or query error
          console.error('Error loading inquiries:', error);
          setContactedVendors([]);
          setLoading(false);
          return;
        }

        const vendors = inquiries?.map(inquiry => ({
          id: inquiry.vendor_profiles.id,
          inquiryId: inquiry.id,
          name: inquiry.vendor_profiles.business_name,
          category: inquiry.vendor_profiles.category,
          contactDate: inquiry.created_at,
          lastResponse: inquiry.updated_at !== inquiry.created_at ? inquiry.updated_at : null,
          status: inquiry.status || 'pending',
          message: inquiry.message?.substring(0, 100) || 'Inquiry sent',
          avatar: '/assets/images/no_image.png',
          responseTime: null
        })) || [];

        setContactedVendors(vendors);
      } catch (error) {
        console.error('Error loading inquiries:', error);
        setContactedVendors([]);
      } finally {
        setLoading(false);
      }
    };

    loadInquiries();
  }, [user]);

  // Use real data from database
  const displayVendors = contactedVendors;

  const [activeFilter, setActiveFilter] = useState('all');

  const getStatusColor = (status) => {
    switch (status) {
      case 'responded': return 'bg-green-100 text-green-600';
      case 'pending': return 'bg-yellow-100 text-yellow-600';
      case 'quote_sent': return 'bg-blue-100 text-blue-600';
      case 'scheduled': return 'bg-purple-100 text-purple-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'responded': return 'MessageCircle';
      case 'pending': return 'Clock';
      case 'quote_sent': return 'FileText';
      case 'scheduled': return 'Calendar';
      default: return 'Circle';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'responded': return 'Responded';
      case 'pending': return 'Pending';
      case 'quote_sent': return 'Quote Sent';
      case 'scheduled': return 'Scheduled';
      default: return 'Unknown';
    }
  };

  const getFilteredVendors = () => {
    if (activeFilter === 'all') return displayVendors;
    return displayVendors.filter(vendor => vendor.status === activeFilter);
  };

  const handleFollowUp = (vendorId) => {
    onSectionChange?.('messages');
  };

  const handleViewDetails = (vendor) => {
    navigate('/vendor-profile', {
      state: {
        vendorId: vendor.id,
        vendorName: vendor.name,
        category: vendor.category
      }
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-soft p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            Quick Contact Summary
          </h3>
          <p className="text-text-secondary text-sm">
            Track your vendor communications
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="MessageSquare"
            onClick={() => onSectionChange?.('messages')}
          >
            All Messages
          </Button>
        </div>
      </div>

      {/* Status Filters */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All', count: displayVendors.length },
            { key: 'pending', label: 'Pending', count: displayVendors.filter(v => v.status === 'pending').length },
            { key: 'responded', label: 'Responded', count: displayVendors.filter(v => v.status === 'responded').length },
            { key: 'quote_sent', label: 'Quoted', count: displayVendors.filter(v => v.status === 'quote_sent').length },
            { key: 'scheduled', label: 'Scheduled', count: displayVendors.filter(v => v.status === 'scheduled').length }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${activeFilter === filter.key
                ? 'bg-primary text-white' : 'bg-surface-200 text-text-secondary hover:bg-surface-300'
                }`}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>
      </div>

      {/* Vendor List */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {loading ? (
          <div className="text-center py-8 text-text-secondary">
            <Icon name="Loader" className="w-8 h-8 mx-auto mb-2 animate-spin" />
            <p>Loading inquiries...</p>
          </div>
        ) : getFilteredVendors().length === 0 ? (
          <div className="text-center py-8 text-text-secondary">
            <Icon name="MessageCircle" className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No inquiries found</p>
            <p className="text-sm mt-1">Start browsing vendors to send inquiries</p>
          </div>
        ) : (
          getFilteredVendors().map((vendor) => (
            <div key={vendor.id} className="border border-border-light rounded-lg p-4 hover:bg-surface-50 transition-colors duration-200">
              <div className="flex items-start space-x-3">
                <img
                  src={vendor.avatar}
                  alt={vendor.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <h4 className="font-medium text-text-primary">{vendor.name}</h4>
                      <p className="text-sm text-text-secondary">{vendor.category}</p>
                    </div>
                    <div className="text-right">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(vendor.status)}`}>
                        <Icon name={getStatusIcon(vendor.status)} size={12} className="mr-1" />
                        {getStatusText(vendor.status)}
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-text-muted mb-2 line-clamp-1">
                    {vendor.message}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-xs text-text-muted">
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={12} />
                        <span>Contacted: {formatDate(vendor.contactDate)}</span>
                      </div>
                      {vendor.lastResponse && (
                        <div className="flex items-center space-x-1">
                          <Icon name="MessageCircle" size={12} />
                          <span>Last response: {formatDate(vendor.lastResponse)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(vendor)}
                      >
                        View
                      </Button>
                      {vendor.status === 'pending' && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleFollowUp(vendor.id)}
                        >
                          Follow Up
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default QuickContactSummary;